import { useState, useEffect } from "react";
import axios from "axios";
import { Building2, Globe, Users, ArrowLeft, ExternalLink, Shield, Award, Package, BarChart3 } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { AICCIMethodologyLink } from "../dashboard/AICCIMethodology";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const OC = { care: "#10b981", utility: "#3b82f6", creative: "#a855f7", extraction: "#ef4444" };
const OC_LABELS = { care: "Care", utility: "Utility", creative: "Creative", extraction: "Extraction" };

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#0c1222] border border-white/10 rounded-lg px-3 py-2 text-xs shadow-xl">
      <p className="font-bold text-white mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color || "#94a3b8" }}>{p.name}: {typeof p.value === "number" ? p.value.toLocaleString() : p.value}</p>
      ))}
    </div>
  );
};

export const CompanyDetail = ({ domain, onBack, onCompare }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!domain) return;
    setLoading(true);
    axios.get(`${API}/companies/detail/${encodeURIComponent(domain)}`)
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [domain]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="w-10 h-10 rounded-full border-2 border-amber-500/30 border-t-amber-500 animate-spin" />
      </div>
    );
  }

  if (!data || data.error) {
    return (
      <div className="text-center py-20">
        <p className="text-sm text-slate-400">Company not found</p>
        <button onClick={onBack} className="mt-4 text-xs text-amber-400 hover:text-amber-300">Back to leaderboard</button>
      </div>
    );
  }

  const od = data.orientation_distribution || {};
  const orientData = Object.entries(od).filter(([, v]) => v > 0).map(([k, v]) => ({ name: OC_LABELS[k] || k, value: Math.round(v), fill: OC[k] || "#475569" }));
  const products = data.product_details || [];
  // Accept either legacy `company_cii` (0-20) or newer `cii_score` (0-100).
  // If only the newer one exists, use it as-is; otherwise fall back to the raw.
  const ciiRaw = data.cii_score ?? data.company_cii ?? 0;
  const cii = Math.max(0, Math.round(ciiRaw * 10) / 10);
  const ciiColor = cii >= 30 ? "#10b981" : cii >= 15 ? "#f59e0b" : "#ef4444";

  return (
    <div className="space-y-6 animate-fade-in pb-8" data-testid="company-detail-page">
      {/* Back button */}
      <button onClick={onBack} className="flex items-center gap-2 text-xs text-slate-400 hover:text-white transition-colors" data-testid="back-to-companies">
        <ArrowLeft className="w-4 h-4" /> Back to Companies
      </button>

      {/* Hero */}
      <div className="relative rounded-2xl overflow-hidden" style={{ background: "linear-gradient(135deg, #0a0f1a 0%, #1a1015 50%, #0f0a1a 100%)", border: "1px solid rgba(245,158,11,0.12)" }}>
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse at 20% 50%, rgba(245,158,11,0.05), transparent 60%)" }} />
        <div className="relative px-8 py-8">
          <div className="flex flex-col md:flex-row md:items-start justify-between gap-6">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: `${OC[data.dominant_orientation]}15`, border: `1px solid ${OC[data.dominant_orientation]}30` }}>
                  <Building2 className="w-6 h-6" style={{ color: OC[data.dominant_orientation] }} />
                </div>
                <div>
                  <h1 className="text-2xl sm:text-3xl font-black text-white">{data.company_name}</h1>
                  <div className="flex items-center gap-3 mt-1">
                    <a href={`https://${data.domain}`} target="_blank" rel="noreferrer" className="text-xs text-amber-400/70 hover:text-amber-400 flex items-center gap-1">
                      {data.domain} <ExternalLink className="w-3 h-3" />
                    </a>
                    <span className="text-[9px] px-2 py-0.5 rounded-full" style={{ color: OC[data.dominant_orientation], background: `${OC[data.dominant_orientation]}15`, border: `1px solid ${OC[data.dominant_orientation]}25` }}>
                      {OC_LABELS[data.dominant_orientation]}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap gap-4 text-xs text-slate-400 mt-4">
                {data.country && <span className="flex items-center gap-1"><Globe className="w-3 h-3" />{data.country}</span>}
                {data.industry && <span className="flex items-center gap-1"><BarChart3 className="w-3 h-3" />{data.industry}</span>}
                {data.founder_composition && <span className="flex items-center gap-1"><Users className="w-3 h-3" />{data.founder_composition.replace("_", " ")}</span>}
              </div>
            </div>

            {/* CII Ring */}
            <div className="flex items-center gap-4 flex-shrink-0">
              <div className="relative w-20 h-20 flex items-center justify-center">
                <svg width="80" height="80" viewBox="0 0 80 80">
                  <circle cx="40" cy="40" r="34" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="5" />
                  <circle cx="40" cy="40" r="34" fill="none" stroke={ciiColor} strokeWidth="5"
                    strokeDasharray={`${cii * 2.14} ${214 - cii * 2.14}`}
                    strokeDashoffset="54" strokeLinecap="round" style={{ transition: "stroke-dasharray 1s ease" }} />
                </svg>
                <span className="absolute text-xl font-black" style={{ color: ciiColor }}>{cii}</span>
              </div>
              <div>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Company CII</p>
                <AICCIMethodologyLink />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { icon: Package, label: "Products", value: data.product_count, color: "#f59e0b" },
          { icon: Shield, label: "Software", value: data.software_count || 0, color: "#10b981" },
          { icon: Award, label: "Hardware", value: data.hardware_count || 0, color: "#3b82f6" },
          { icon: Globe, label: "Country", value: data.country || "Unknown", color: "#22d3ee" },
        ].map(m => (
          <div key={m.label} className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-4">
            <div className="flex items-center gap-2 mb-1">
              <m.icon className="w-3.5 h-3.5" style={{ color: m.color }} />
              <span className="text-[9px] font-bold uppercase tracking-wider" style={{ color: `${m.color}90` }}>{m.label}</span>
            </div>
            <p className="text-xl font-black text-white">{typeof m.value === "number" ? m.value.toLocaleString() : m.value}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Orientation Breakdown */}
        <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5">
          <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4">Value Orientation Breakdown</h3>
          <div className="space-y-3">
            {orientData.map(d => (
              <div key={d.name} className="flex items-center gap-3">
                <span className="text-[10px] text-slate-400 w-20 flex-shrink-0">{d.name}</span>
                <div className="flex-1 h-3 bg-white/[0.04] rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all duration-700" style={{ width: `${d.value}%`, background: d.fill }} />
                </div>
                <span className="text-xs font-bold w-10 text-right" style={{ color: d.fill }}>{d.value}%</span>
              </div>
            ))}
          </div>
        </div>

        {/* Industries */}
        {data.industries && Object.keys(data.industries).length > 0 && (
          <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5">
            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4">Industry Presence</h3>
            <div className="h-48">
              <ResponsiveContainer>
                <BarChart data={Object.entries(data.industries).map(([k, v]) => ({ name: k, count: v }))} layout="vertical" margin={{ left: 100 }}>
                  <XAxis type="number" tick={{ fill: "#64748b", fontSize: 10 }} />
                  <YAxis type="category" dataKey="name" tick={{ fill: "#94a3b8", fontSize: 10 }} width={100} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" name="Products" fill="#a855f7" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>

      {/* Products List */}
      <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl overflow-hidden" data-testid="company-products">
        <div className="px-5 py-4 border-b border-white/[0.06]">
          <h3 className="text-sm font-bold text-white">Products ({products.length})</h3>
          <p className="text-[10px] text-slate-500">AI products built by {data.company_name}</p>
        </div>
        <div className="divide-y divide-white/[0.03]">
          {products.length === 0 && <p className="text-xs text-slate-500 p-5 text-center">No product details available</p>}
          {products.map((p, i) => (
            <div key={p.product_name || i} className="px-5 py-3 hover:bg-white/[0.02] transition-colors">
              <div className="flex items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-white truncate">{p.product_name}</p>
                  {p.tagline && <p className="text-[10px] text-slate-400 truncate mt-0.5">{p.tagline}</p>}
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  {p.industry && <span className="text-[9px] px-2 py-0.5 bg-violet-500/10 border border-violet-500/15 rounded-full text-violet-300">{p.industry}</span>}
                  {p.value_orientation && (
                    <span className="text-[9px] px-2 py-0.5 rounded-full" style={{
                      color: OC[p.value_orientation], background: `${OC[p.value_orientation]}12`, border: `1px solid ${OC[p.value_orientation]}25`
                    }}>{OC_LABELS[p.value_orientation]}</span>
                  )}
                  <span className="text-[9px] text-slate-500 uppercase">{p.entity_type === "device" ? "HW" : "SW"}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Compare CTA */}
      {onCompare && (
        <div className="text-center py-4">
          <button onClick={() => onCompare(data.domain)} className="text-xs text-amber-400 hover:text-amber-300 font-medium" data-testid="compare-from-detail">
            Compare {data.company_name} with others →
          </button>
        </div>
      )}
    </div>
  );
};

export default CompanyDetail;
